import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { adminApi } from '@/shared/api/adminApi';
import { useActiveSnoop, type SnoopMode } from '@/shared/stores/activeSnoop';

const MODE_META: Record<SnoopMode, { label: string; verb: string; icon: IconName; desc: string }> = {
  listen: { label: 'האזנה', verb: 'מאזין ל', icon: 'headphones', desc: 'הנציג והלקוח לא שומעים אותך' },
  whisper: { label: 'לחישה', verb: 'לוחש ל', icon: 'mic', desc: 'רק הנציג שומע אותך' },
  barge: { label: 'הצטרפות', verb: 'בשיחה עם', icon: 'bolt', desc: 'כולם שומעים אותך' },
};

/**
 * סרגל שליטה צף על snoop פעיל — מופיע כשמנהל מאזין/לוחש/מצטרף לשיחה, ומאפשר
 * לו לרדת מהשיחה או להחליף מצב (האזנה↔לחישה↔הצטרפות) מכל מסך. מותקן ב-layout
 * (מחוץ ל-Outlet) כדי לשרוד ניווט. נשלט מ-store useActiveSnoop.
 *
 * החלפת מצב = עצירת ה-snoop הנוכחי + התחלת snoop חדש (Asterisk לא מאפשר לשנות
 * spy/whisper על snoop קיים). "רד מהאזנה" מנתק את ערוץ ה-snoop בשרת.
 */
export function SnoopControlBar() {
  const active = useActiveSnoop((s) => s.active);
  const update = useActiveSnoop((s) => s.update);
  const clear = useActiveSnoop((s) => s.clear);

  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const barRef = useRef<HTMLDivElement | null>(null);

  // טיימר חי מרגע ההתחלה.
  useEffect(() => {
    if (!active) return;
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - active.startedAt) / 1000)));
    tick();
    const h = setInterval(tick, 1000);
    return () => clearInterval(h);
  }, [active?.startedAt]);

  if (!active) return null;

  const meta = MODE_META[active.mode];

  // החלפת מצב: עוצר את ה-snoop הנוכחי, מתחיל חדש עם ה-mode החדש.
  async function switchMode(mode: SnoopMode) {
    if (!active || busy || mode === active.mode) return;
    setBusy(true);
    try {
      await adminApi.stopSnoop(active.snoopChannelId);
      const res = await adminApi.snoop(active.channelId, mode);
      if (res?.success) {
        update({ mode, snoopChannelId: res?.data?.snoop_channel_id ?? null, startedAt: Date.now() });
        toast(`${MODE_META[mode].label} פעיל`, 'ok');
      } else {
        toast(res?.message ?? 'החלפת המצב נכשלה', 'danger');
        clear(); // ה-snoop הישן כבר נותק — לא להשאיר מצב שגוי
      }
    } catch {
      toast('שגיאה בהחלפת מצב', 'danger');
    } finally {
      setBusy(false);
    }
  }

  // "רד מהאזנה" — מנתק בשרת וסוגר את הסרגל.
  async function descend() {
    if (!active || busy) return;
    setBusy(true);
    try {
      await adminApi.stopSnoop(active.snoopChannelId);
      toast('ירדת מהשיחה', 'ok');
    } catch {
      // גם אם הניתוק נכשל — סוגרים את הסרגל (המנהל יכול לנתק ידנית).
    } finally {
      clear();
      setBusy(false);
    }
  }

  return createPortal(
    <div
      ref={barRef}
      role="region"
      aria-label="שליטה בהאזנה פעילה"
      style={{
        position: 'fixed', insetInline: 0, top: 0, zIndex: 1250,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '8px 16px',
        background: active.mode === 'barge' ? 'var(--danger)' : 'var(--accent)',
        color: 'var(--on-accent, #fff)',
        boxShadow: 'var(--shadow-2, 0 4px 16px rgba(0,0,0,.2))',
      }}
    >
      {/* מצב נוכחי + שם נציג + ping-dot חי */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ position: 'relative', display: 'inline-flex', width: 10, height: 10 }}>
          <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#fff', opacity: 0.75, animation: 'snoopPing 1.4s cubic-bezier(0,0,.2,1) infinite' }} />
          <span style={{ position: 'relative', display: 'inline-flex', width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
        </span>
        <Icon name={meta.icon} size={16} color="#fff" />
        <strong style={{ fontSize: 'var(--t-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {meta.verb} {active.agentName}
        </strong>
        <span dir="ltr" style={{ fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)', opacity: 0.9 }}>{fmt(elapsed)}</span>
      </div>

      <span style={{ flex: 1 }} />

      {/* כפתורי החלפת מצב */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {(['listen', 'whisper', 'barge'] as SnoopMode[]).map((m) => {
          const on = m === active.mode;
          return (
            <button
              key={m} type="button" disabled={busy} onClick={() => switchMode(m)}
              title={MODE_META[m].desc}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-xs)', fontWeight: 600,
                padding: '5px 10px', borderRadius: 'var(--r-2)', cursor: busy ? 'default' : 'pointer',
                border: '1px solid rgba(255,255,255,.5)',
                background: on ? '#fff' : 'transparent',
                color: on ? (active.mode === 'barge' ? 'var(--danger)' : 'var(--accent)') : '#fff',
                opacity: busy && !on ? 0.6 : 1,
              }}
            >
              <Icon name={MODE_META[m].icon} size={13} color={on ? (active.mode === 'barge' ? 'var(--danger)' : 'var(--accent)') : '#fff'} />
              {MODE_META[m].label}
            </button>
          );
        })}
      </div>

      {/* רד מהשיחה (בולט) */}
      <button
        type="button" disabled={busy} onClick={descend}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)', fontWeight: 700, padding: '6px 14px', borderRadius: 'var(--r-2)', border: 'none', background: '#fff', color: 'var(--danger)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}
      >
        <Icon name="hang" size={14} color="var(--danger)" /> רד מהשיחה
      </button>

      <style>{'@keyframes snoopPing{75%,100%{transform:scale(2.2);opacity:0}}'}</style>
    </div>,
    document.body,
  );
}

function fmt(s: number): string {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}
